import { MASProgramExtrasModel, Notifications, Program, SkillGroup } from '.';
import { MAS_Accounts_Service } from '../services';
import { AdvancedDebugging, AnyObject, DateTimeUtils, findNestedObjectDiff, findObjectDiffSummary, masIdsEqual } from '../utilities';

/** Account-level staff notes; one current value per comment kind. */
export type AccountComments = {
	trialOfferComment?: string;
};

export interface Account extends AccountComments, Addresses, Birthdays, Emails, AltenrateEmails, AlternateNames, AltenratePhoneNumbers, MAS, Memberships, Names, PhoneNumbers, URLS {
	id: string;
	standings?: TournamentStanding[];
}

export interface AccountAttendance {
	cycleName: string;
	scheduleId: string;
	scheduleName: string;
	startTime: string;
}

export type AccountSettings = {
	activeDate?: string;
	alternateTestCategory?: string;
	billing: 'true' | 'false';
	enableEmailReminders?: boolean;
	enableSMS?: boolean;
	gender: string;
	inactiveDate?: string;
	legacy?: 'student' | 'leadership' | 'level-0' | 'level-1' | 'level-2' | 'level-3' | 'staff' | 'none';
	member: 'true' | 'false';
	primaryTestCategory?: string;
	profilePicture?: string;
	school?: string;
	status: string;
	timeCreated?: string;
	trialConversion?: boolean;
	trialEndDate?: string;
};

export interface AccountEditMode {
	accountType?: 'active' | 'addParent' | 'child' | 'existing' | 'inactive' | 'legacy' | 'new' | 'schedules' | 'violations' | 'yas';
	editState: boolean;
	account?: Account;
}

export interface AccountFilter {
	add: string;
	default: string;
	inactive: string;
	new: string;
	pending: string;
	schedules: string;
	violations: string;
	yas: string;
}

export interface Addresses {
	addresses?: {
		city: string;
		postalCode: string;
		region: string;
		streetAddress: string;
		type?: string;
	};
}

export interface AttendanceViolation {
	action?: 'waive' | 'fee';
	cycleName: string;
	endDate: string;
	id?: string;
	notifications: Notifications;
	scheduleId: string;
	summary: string;
}

export interface Birthdays {
	birthdays?: { text: string };
}

export interface Emails {
	emailAddresses?: {
		type: string;
		value: string;
	};
}

export interface AltenrateEmails {
	emailAlternate?: {
		type: string;
		value: string;
	};
}

export interface gapi extends gapiToken, gapiUser {
	masAccountId?: string;
	userCredential?: any;
}

export interface gapiToken {
	token: {
		access_token: string;
		expiry_date: number;
		id_token: string;
		refresh_token: string;
		scope: string;
		token_type: string;
	};
}

export interface gapiUser {
	user: {
		email: string;
		id: string;
	};
}

export interface LinkedAccounts {
	accountId: string;
	person: string;
	type: 'parent' | 'child';
}

export interface MAS {
	mas: {
		accountSettings: AccountSettings;
		acknowledgements?: string[];
		ataNumber?: string;
		gapi?: gapi;
		linkedAccounts?: LinkedAccounts[];
		memberAttendanceCumulative?: { [key: string]: number };
		nodeType: 'parent' | 'child';
		skills?: SkillGroup[];
		tournaments?: 'blue' | 'red' | 'silver' | 'gold' | 'none' | 'jahngsoo';
		triviaQuestionData?: TriviaQuestionData;
		highLevelId?: string;
	};
}

export interface Memberships {
	memberships?: string[];
}

export interface Names {
	names: {
		familyName: string;
		givenName: string;
		pronunciation?: string;
	};
}

export interface AlternateNames {
	namesAlternate?: {
		familyName: string;
		givenName: string;
		pronunciation?: string;
	};
}

/**
 * @deprecated Use `NewAccountInterface` instead.
 */
export interface Node {
	billing: string;
	children?: unknown[];
	collapsedIcon?: 'pi pi-user';
	data: Account;
	expanded?: null;
	expandedIcon?: 'pi pi-users';
	key: string;
	label: string;
	status: string;
}

export interface PhoneNumbers {
	phoneNumbers?: {
		type: string;
		value: string;
	};
}

export interface AltenratePhoneNumbers {
	phoneNumbersAlternate?: {
		type: string;
		value: string;
	};
}

export interface TournamentParticipation {
	tournamentDate: string;
	tournamentName: string;
}
export interface TournamentStanding {
	ageGroup: '1-8' | '9-10' | '11-12' | '13-14' | '15-17' | '18-29' | '30-39' | '40-49' | '50-59' | '60-69' | '70+';
	archive: boolean;
	ataName: string;
	ataNumber: string;
	currentStanding: number;
	event: 'Combat Weapons' | 'Creative Forms' | 'Creative Weapons' | 'Forms' | 'Sparring' | 'Weapons Forms' | 'X-treme Forms' | 'X-treme Weapons';
	points: number;
	rankGroup: '1DB' | '1DB/2DB/3DB' | '2DB/3DB' | '4DB/5DB' | 'COLOR' | 'SPECIAL';
	season: '2023-2024' | '2024-2025';
}

export interface TriviaQuestionData {
	cumulativeCorrect: number;
	lastCorrectDate: string;
	lastDate: string;
}

export interface URLS {
	urls?: any;
}
export interface ViewStandingsByMember {
	name: string;
	data: TournamentStanding[];
}

/**
 * Account view-model + edit session (not the persistence layer).
 * Region guide: `docs/class-model-account-view-model.md`.
 */
export class MASAccountModel {
	#data: Account;
	children?: MASAccountModel[];

	private advancedDebugging = new AdvancedDebugging().closeNDeep(10).setLogStyle('color: lightpurple;');

	constructor(
		data: Account,
		private accountService: MAS_Accounts_Service,
	) {
		this.#data = data;
	}

	// --- Identity & display (view) ---

	get accountSettings() {
		return this.raw.mas.accountSettings;
	}

	get activeDateDisplay(): string {
		const val = this.raw.mas.accountSettings.trialEndDate;
		return val ? (DateTimeUtils.formatDateStringByMode(val, 'display') ?? '') : '';
	}

	get age(): number | null {
		const birthday = this.raw.birthdays?.text;
		if (!birthday) return null;
		return DateTimeUtils.calculateAge(birthday);
	}

	get alternateTestCategory(): string {
		return this.raw.mas.accountSettings.alternateTestCategory?.trim() || 'None';
	}

	set alternateTestCategory(value: string) {
		this.raw.mas.accountSettings.alternateTestCategory = value;
	}

	get birthdayAsDate(): Date | null {
		const val = this.raw.birthdays?.text;
		return val ? DateTimeUtils.parseDate(val) : null;
	}

	get birthdayDisplay(): string {
		const val = this.raw.birthdays?.text;
		return val ? (DateTimeUtils.formatDateStringByMode(val, 'display') ?? '') : '';
	}

	get emailAddress(): string {
		return this.raw.emailAddresses?.value || '';
	}

	get fullName(): string {
		const { givenName = '', familyName = '' } = this.#data.names || {};
		return `${givenName} ${familyName}`.trim();
	}

	get pronunciation(): string {
		return this.#data.names.pronunciation || '';
	}

	get fullAlternateName(): string {
		const { givenName = '', familyName = '' } = this.#data.namesAlternate || {};
		return `${givenName} ${familyName}`.trim();
	}

	get alternatepronunciation(): string {
		return this.#data.namesAlternate?.pronunciation || '';
	}

	get hasSchool(): boolean {
		const isChild = this.raw?.mas?.nodeType === 'child';
		const birthdayStr = this.raw?.birthdays?.text;
		const age = birthdayStr ? DateTimeUtils.calculateAge(birthdayStr) : null;

		return isChild && age !== null && age < 18;
	}

	get id(): string {
		return this.#data.id;
	}

	get inactiveDateDisplay(): string {
		const val = this.raw.mas.accountSettings.inactiveDate;
		return val ? (DateTimeUtils.formatDateStringByMode(val, 'display') ?? '') : '';
	}

	get isBilling(): boolean {
		return this.#data?.mas?.accountSettings?.billing === 'true';
	}

	get isChild(): boolean {
		return this.#data.mas.nodeType === 'child';
	}

	get isInactive(): boolean {
		return this.status === 'Inactive';
	}

	get isInstructor(): boolean {
		return this.#data.mas.accountSettings.legacy === 'level-3';
	}

	get isMember(): boolean {
		return this.#data.mas.accountSettings.member === 'true';
	}

	get isParent(): boolean {
		return this.#data.mas.nodeType === 'parent';
	}

	get isStaff(): boolean {
		return this.#data.mas.accountSettings.legacy === 'staff';
	}

	get isTrial(): boolean {
		return this.#data.mas.accountSettings.status === 'Trial';
	}

	get legacy(): string {
		return this.raw.mas.accountSettings.legacy?.trim() || 'none';
	}

	set legacy(value: 'student' | 'leadership' | 'level-0' | 'level-1' | 'level-2' | 'level-3' | 'staff' | 'none') {
		this.raw.mas.accountSettings.legacy = value;
	}

	get memberships(): string[] {
		return this.raw.memberships || [];
	}

	get phoneNumber(): string {
		return this.#data.phoneNumbers?.value ?? '';
	}

	get raw(): Account {
		return this.#data;
	}

	get status(): string {
		return this.#data.mas.accountSettings.status;
	}

	get school(): string {
		return this.#data.mas.accountSettings.school ?? '';
	}

	get trialEndDateAsDate(): Date | null {
		const val = this.raw.mas.accountSettings.trialEndDate;
		return val ? DateTimeUtils.parseDate(val) : null;
	}

	get trialEndDateDisplay(): string {
		const val = this.raw.mas.accountSettings.trialEndDate;
		return val ? (DateTimeUtils.formatDateStringByMode(val, 'display') ?? '') : '';
	}

	get trialOfferComment(): string {
		return this.#data.trialOfferComment ?? '';
	}

	set trialOfferComment(value: string) {
		this.#data.trialOfferComment = value;
	}

	set trialConversion(value: boolean) {
		this.raw.mas.accountSettings.trialConversion = value;
	}

	setStandings(standings: TournamentStanding[]): void {
		this.#data.standings = standings;
	}

	// --- Local edit (in-memory; facade persists) ---

	async addProgram(program: Program): Promise<this> {
		const programId = program.id as string;
		this.raw.memberships = this.raw.memberships ? this.raw.memberships.concat([programId]) : [programId];
		return this;
	}

	applyEditModeType(type: 'child' | 'addParent' | 'existing' | string | undefined) {
		if (type === 'child') this.raw.mas.nodeType = 'child';
		else if (type === 'addParent') this.raw.mas.nodeType = 'parent';
	}

	// --- Rules & normalization ---

	autoFix(): string[] {
		this.advancedDebugging.setLogStyle('color: pink; background: purple');
		const violations: string[] = [];
		const { mas } = this.raw;
		mas.linkedAccounts ??= [];

		// Validate settings object first
		mas.accountSettings ??= {
			billing: 'false',
			gender: 'unspecified',
			member: 'false',
			status: 'Inactive',
			legacy: 'none',
		};

		const { nodeType, accountSettings } = mas;
		const linkedAccounts = mas.linkedAccounts;
		const isChild = nodeType === 'child';
		const isParent = nodeType === 'parent';
		const isMember = accountSettings.member === 'true';
		const age = this.age;

		// Normalize and deduplicate linkedAccounts first
		mas.linkedAccounts = linkedAccounts
			.filter(acc => acc?.accountId)
			.filter((acc, i, arr) => arr.findIndex(a => masIdsEqual(a.accountId, acc.accountId)) === i);

		// Clean up obsolete `data` field
		mas.linkedAccounts = mas.linkedAccounts.map(acc => {
			const { data, ...cleaned } = acc as any;
			return cleaned;
		});

		if (isChild) {
			// Valid parent entries only
			mas.linkedAccounts = mas.linkedAccounts.filter(acc => acc.type === 'parent');

			if (mas.linkedAccounts.length !== 1 && accountSettings.status === 'Active') {
				violations.push('An active child must have exactly one valid parent-linked account.');
			}

			if (mas.linkedAccounts.length === 1 && mas.linkedAccounts[0].type !== 'parent') {
				mas.linkedAccounts[0].type = 'parent';
			}

			if (accountSettings.billing !== 'false') accountSettings.billing = 'false';

			if (!isMember) accountSettings.member = 'true';

			if ((age !== null && age < 18 && !accountSettings.school) || !('school' in accountSettings)) {
				this.advancedDebugging.log(`Fixing missing school for child ${this.id}`);
				accountSettings.school = 'Other';
			}

			if (this.raw.emailAddresses) {
				delete this.raw.emailAddresses;
			}
		}

		if (isParent) {
			// All child types, deduplicated
			mas.linkedAccounts = mas.linkedAccounts.map(acc => ({ ...acc, type: 'child' as const })).filter((acc, i, arr) => arr.findIndex(a => a.accountId === acc.accountId) === i);

			if (!isMember) {
				if (accountSettings.status === 'Trial') {
					accountSettings.status = 'Active';
				}
				if ('birthdays' in this.raw) {
					this.advancedDebugging.log(`Removing birthday from non-member parent ${this.id}`);
					delete this.raw.birthdays;
				}
				if ('trialEndDate' in accountSettings) {
					this.advancedDebugging.log(`Removing Trial End Date from non-member parent ${this.id}`);
					delete accountSettings.trialEndDate;
				}
				if (!Array.isArray(this.raw.memberships)) {
					this.advancedDebugging.log(`Fixing memberships property on non-member ${this.id}`);
					this.raw.memberships = [];
				}
			}
		}

		// MEMBERSHIP FIXES
		if (isMember) {
			const t = mas.tournaments;
			const isInvalid = !t || typeof t !== 'string' || t.trim().length === 0;
			if (isInvalid) {
				this.advancedDebugging.log(`Fixing tournaments on ${this.id}`, { originalValue: t });
				mas.tournaments = 'none';
			}
			if (!('alternateTestCategory' in accountSettings)) {
				this.advancedDebugging.log(`Fixing missing alternateTestCategory on member ${this.id}`);
				accountSettings.alternateTestCategory = 'None';
			}
			if (!('legacy' in accountSettings)) {
				this.advancedDebugging.log(`Fixing missing legacy status on member ${this.id}`);
				accountSettings.legacy = 'student';
			}
			if (accountSettings.status === 'Trial' && (accountSettings.trialConversion === true || accountSettings.trialConversion === undefined)) {
				accountSettings.trialConversion = false;
			}
			if (accountSettings.status === 'Active' && (accountSettings.trialConversion === false || accountSettings.trialConversion === undefined)) {
				accountSettings.trialConversion = true;
			}
			if (accountSettings.status === 'Active' && 'timeCreated' in accountSettings && !('activeDate' in accountSettings)) {
				this.advancedDebugging.log(`Setting activeDate from timeCreated on member ${this.id}`, {
					timeCreated: accountSettings.timeCreated,
				});
				accountSettings.activeDate = accountSettings.timeCreated;
			}
		} else {
			// ONLY run this if NOT a member
			if ('alternateTestCategory' in accountSettings) {
				this.advancedDebugging.log(`Removing alternateTestCategory from non-member ${this.id}`);
				delete accountSettings.alternateTestCategory;
			}
			if ('tournaments' in mas) {
				this.advancedDebugging.log(`Removing tournament data from non-member ${this.id}`);
				delete mas.tournaments;
			}
			if (!accountSettings.legacy) {
				this.advancedDebugging.log(`Fixing missing legacy status ${this.id}`);
				accountSettings.legacy = 'none';
			}
		}

		const result = this.validate();
		return result.violations;
	}

	clearLinks(): this {
		this.raw.mas.linkedAccounts = [];
		return this;
	}

	clearMemberships(): this {
		this.raw.memberships = [];
		return this;
	}

	clone(): MASAccountModel {
		const clonedRaw: Account = JSON.parse(JSON.stringify(this.raw));
		return new MASAccountModel(clonedRaw, this.accountService);
	}

	correctLinkName(account: MASAccountModel): this {
		const links = this.raw.mas?.linkedAccounts;
		if (Array.isArray(links)) {
			links.forEach(link => {
				if (masIdsEqual(link.accountId, account.raw.id)) {
					link.person = account.fullName;
				}
			});
		}
		return this;
	}

	// --- Diff & audit helpers (no I/O; see object-diff.utilities) ---

	diffBetweenSnapshots(a: Account, b: Account): Record<string, { from: any; to: any }> {
		return findObjectDiffSummary(a, b) as Record<string, { from: any; to: any }>;
	}

	diffRaw(otherRaw: Account): AnyObject {
		return findNestedObjectDiff(this.raw, otherRaw) || {};
	}

	diffSummaryRaw(aOrOtherRaw: Account | any, bRaw?: any): Record<string, { from: any; to: any }> {
		const a = bRaw ? aOrOtherRaw : this.raw;
		const b = bRaw ? bRaw : aOrOtherRaw;
		return findObjectDiffSummary(a, b) as Record<string, { from: any; to: any }>;
	}

	async getLinkedAccounts(): Promise<MASAccountModel[]> {
		const ids = this.#data.mas.linkedAccounts?.map(x => x.accountId).filter(Boolean);
		if (!ids?.length) return [];
		const accounts = await this.accountService.getAccountsByLinked(ids);
		return accounts.map(acc => new MASAccountModel(acc, this.accountService));
	}

	hasChildren(): boolean {
		return !!this.#data.mas.linkedAccounts?.length;
	}

	private isDate(value: unknown): value is Date {
		return value instanceof Date && !isNaN(value.getTime());
	}

	linkAccount(account: Account): void {
		this.advancedDebugging.setLogStyle('color: pink; background: purple');
		try {
			this.advancedDebugging.log('MASAccountModel=>linkAccount=>account', account);

			const newLink: LinkedAccounts = {
				accountId: account.id,
				person: `${account.names.givenName} ${account.names.familyName}`,
				type: account.mas.nodeType as 'parent' | 'child',
			};
			this.advancedDebugging.log('MASAccountModel=>linkAccount=>newLink', newLink);

			if (!this.raw.mas.linkedAccounts) {
				this.raw.mas.linkedAccounts = [];
			}

			// Avoid duplicate linking
			const alreadyLinked = this.raw.mas.linkedAccounts.some(link => masIdsEqual(link.accountId, newLink.accountId));

			if (!alreadyLinked) {
				this.raw.mas.linkedAccounts.push(newLink);
			}
			this.advancedDebugging.log('MASAccountModel=>mas.linkAccount', this.raw.mas.linkedAccounts);
		} catch (error) {
			this.advancedDebugging.captureError('MASAccountModel=>linkAccount', error, { account });
		}
	}

	removeATANumber(): this {
		return this._removeField('mas.ataNumber');
	}

	removeBirthday(): this {
		return this._removeField('birthdays');
	}

	removeEmail(): this {
		this._removeField('emailAlternate');
		this._removeField('emailAddresses');
		return this;
	}

	removeLinkedAccounts(): this {
		return this._removeField('mas.linkedAccounts');
	}

	removeLinkToChild(childId: string): this {
		this.raw.mas.linkedAccounts = this.raw.mas.linkedAccounts?.filter(link => !masIdsEqual(link.accountId, childId)) ?? [];
		return this;
	}

	removeNotifications(): this {
		this._removeField('mas.accountSettings.enableEmailReminders');
		this._removeField('mas.accountSettings.enableSMS');
		return this;
	}

	async removeProgram(program: Program): Promise<this> {
		this.raw.memberships = this.raw.memberships ? this.raw.memberships.filter(id => !masIdsEqual(id, program.id)) : [];
		return this;
	}

	removeSchool(): this {
		return this._removeField('mas.accountSettings.school');
	}

	removeTrialEndDate(): this {
		return this._removeField('mas.accountSettings.trialEndDate');
	}

	removeInactiveDate(): this {
		return this._removeField('mas.accountSettings.inactiveDate');
	}

	removeTournaments(): this {
		return this._removeField('mas.tournaments');
	}

	/**
	 * @deprecated The property alternateTestCategory is obsolete.
	 */
	removeAlternateTesting(): this {
		return this._removeField('mas.accountSettings.alternateTestCategory');
	}

	// --- Persistence (delegates to MAS_Accounts_Service.persistAccount) ---

	async save(
		state: 'existing' | 'new',
		merge: boolean = true,
		options?: { history: Record<string, any>; autofix?: Record<string, any>; userId: string; caller: string },
	): Promise<Account> {
		return this.accountService.persistAccount(this, state, merge, options);
	}

	/**
	 * @deprecated Use `MAS_Accounts_Service.setAccountAttendance` from the calling facade (no live call sites).
	 */
	async setAttendance(payload: AccountAttendance): Promise<void> {
		try {
			await this.accountService.setAccountAttendance(this.id, payload);
		} catch (error) {
			this.advancedDebugging.captureError('MASAccountModel=>setAttendance', error, { payload });
		}
	}

	setInactiveDate(date?: Date): this {
		this.raw.mas.accountSettings.inactiveDate = DateTimeUtils.getNewYorkDateString(date);
		return this;
	}

	setActiveDate(date?: Date): this {
		this.raw.mas.accountSettings.activeDate = DateTimeUtils.getNewYorkDateString(date);
		return this;
	}

	setTimeCreated(date?: Date): this {
		this.raw.mas.accountSettings.timeCreated = DateTimeUtils.getNewYorkDateString(date);
		return this;
	}

	static createEmptyParent(accountService: MAS_Accounts_Service): MASAccountModel {
		const emptyAccount: Account = {
			id: '',
			names: { givenName: '', familyName: '' },
			emailAddresses: { type: 'other', value: '' },
			phoneNumbers: { type: 'other', value: '' },
			addresses: {
				streetAddress: '',
				city: '',
				region: '',
				postalCode: '',
			},
			mas: {
				accountSettings: {
					billing: 'true',
					member: 'false',
					gender: '',
					status: 'Active',
					school: '',
					trialEndDate: '',
					alternateTestCategory: '',
				},
				tournaments: 'none',
				ataNumber: '',
				nodeType: 'parent',
				linkedAccounts: [],
			},
			memberships: [],
		};
		return new MASAccountModel(emptyAccount, accountService);
	}

	toJSON(): Account {
		return this.#data;
	}

	updateFromForm(formValue: Partial<Account>) {
		this.raw.names = formValue.names ?? this.raw.names;
		this.raw.namesAlternate = formValue.namesAlternate ?? this.raw.namesAlternate;

		this.raw.phoneNumbers = formValue.phoneNumbers ?? this.raw.phoneNumbers;
		this.raw.phoneNumbersAlternate = formValue.phoneNumbersAlternate ?? this.raw.phoneNumbersAlternate;

		this.raw.addresses = formValue.addresses ?? this.raw.addresses;

		if (formValue.emailAddresses?.value) {
			this.raw.emailAddresses = {
				...formValue.emailAddresses,
				value: formValue.emailAddresses.value.toLowerCase(),
			};
		} else {
			this.raw.emailAddresses = formValue.emailAddresses ?? this.raw.emailAddresses;
		}

		if (formValue.emailAlternate?.value) {
			this.raw.emailAlternate = {
				...formValue.emailAlternate,
				value: formValue.emailAlternate.value.toLowerCase(),
			};
		} else {
			this.raw.emailAlternate = formValue.emailAlternate ?? this.raw.emailAlternate;
		}

		this.raw.mas.accountSettings = {
			...this.raw.mas.accountSettings,
			...formValue.mas?.accountSettings,
		};

		this.raw.mas.accountSettings = {
			...this.raw.mas.accountSettings,
			...formValue.mas?.accountSettings,
		};

		// ✅ Fix: Prevent blank tournament values from being saved
		const tournaments = formValue.mas?.tournaments;
		if (tournaments && tournaments.trim() !== '') {
			this.raw.mas.tournaments = tournaments;
		} else {
			delete this.raw.mas.tournaments;
		}

		this.raw.mas.ataNumber = formValue.mas?.ataNumber ?? this.raw.mas.ataNumber;

		if (this.raw.mas.accountSettings.member === 'true') {
			const bday = formValue.birthdays?.text;
			if (this.isDate(bday)) {
				this.raw.birthdays = { text: DateTimeUtils.formatDate(bday, 'date') };
			} else if (bday) {
				this.raw.birthdays = { text: bday };
			} else {
				delete this.raw.birthdays;
			}
		} else {
			delete this.raw.birthdays;
		}

		const trialDate = formValue.mas?.accountSettings?.trialEndDate;
		if (this.isDate(trialDate)) {
			this.raw.mas.accountSettings.trialEndDate = DateTimeUtils.formatDate(trialDate, 'date');
		} else if (typeof trialDate === 'string') {
			this.raw.mas.accountSettings.trialEndDate = trialDate;
		} else {
			delete this.raw.mas.accountSettings.trialEndDate;
		}

		return this;
	}

	validate(): { valid: boolean; violations: string[] } {
		const violations: string[] = [];
		const { mas, emailAddresses, birthdays, memberships } = this.raw;
		const { nodeType, linkedAccounts = [], accountSettings } = mas;
		const isChild = nodeType === 'child';
		const isParent = nodeType === 'parent';
		const isMember = accountSettings.member === 'true';
		const age = this.age;

		if (isChild) {
			if (linkedAccounts.length !== 1) {
				violations.push('Child must have exactly one linked account.');
			}

			const hasWrongType = linkedAccounts.some(acc => acc.type !== 'parent');
			if (hasWrongType) {
				violations.push('Child must only be linked to parent accounts.');
			}

			const duplicateIds = linkedAccounts.map(a => a.accountId).filter((id, i, arr) => id && arr.indexOf(id) !== i);
			if (duplicateIds.length > 0) {
				violations.push('Child linkedAccounts must not contain duplicates.');
			}

			if (linkedAccounts.some(acc => 'data' in acc)) {
				violations.push('Child linkedAccounts must not contain the "data" property.');
			}

			if (accountSettings.billing !== 'false') {
				violations.push('Child cannot have billing set to true.');
			}

			if (!isMember) {
				violations.push('Child must have member set to true.');
			}

			if (age !== null && age < 18 && !accountSettings.school) {
				violations.push(`${this.fullName} ${this.raw.id} - Child under 18 must have a school specified.`);
			}

			if (emailAddresses) {
				violations.push('Child account cannot have email addresses.');
			}
		}

		if (isParent) {
			const safeLinkedAccounts = linkedAccounts ?? [];

			if (safeLinkedAccounts.some(acc => acc.type !== 'child')) {
				violations.push('Parent must only be linked to child accounts.');
			}

			const duplicateIds = safeLinkedAccounts.map(a => a.accountId).filter((id, i, arr) => id && arr.indexOf(id) !== i);

			if (duplicateIds.length > 0) {
				violations.push('Parent linkedAccounts must not contain duplicates.');
			}

			if (safeLinkedAccounts.some(acc => 'data' in acc)) {
				violations.push('Parent linkedAccounts must not contain the "data" property.');
			}

			if (!isMember) {
				if (birthdays) {
					violations.push('Non-member parent should not have a birthday.');
				}
				if (accountSettings.status === 'Trial') {
					violations.push('Non-member parent cannot have status "Trial".');
				}
				if ('trialEndDate' in accountSettings && accountSettings.trialEndDate) {
					violations.push('Non-member parent should not have a trialEndDate.');
				}
				if ('tournaments' in mas) {
					violations.push('Non-member parent should not have tournament data.');
				}
				if ((memberships ?? []).length > 0) {
					violations.push('Non-member parent should not have memberships.');
				}
			}

			if ('school' in accountSettings && accountSettings.school) {
				violations.push('Parent account should not have a school assigned.');
			}

			if (!emailAddresses) {
				violations.push('Parent account should have an email address.');
			}
		}

		if (isMember) {
			if (!birthdays?.text) {
				violations.push('Member account should have a birthday.');
			}

			const VALID_TOURNAMENTS = ['blue', 'red', 'silver', 'gold', 'none', 'jahngsoo'];
			const tournament = mas.tournaments;
			if (!VALID_TOURNAMENTS.includes(tournament as string)) {
				violations.push('Member account must have a valid tournament selection.');
			}
			if (!accountSettings.alternateTestCategory) {
				violations.push('Member account should have an alternate test category.');
			}
			if (accountSettings.status === 'Trial' && (accountSettings.trialConversion === true || accountSettings.trialConversion === undefined)) {
				violations.push('Member accounts with a status of Trial cannot have a trialConversion = true');
			}
			if (accountSettings.status === 'Active' && (accountSettings.trialConversion === false || accountSettings.trialConversion === undefined)) {
				violations.push('Member accounts with a status of Active cannot have a trialConversion = false');
			}
			if (accountSettings.status === 'Active' && 'timeCreated' in accountSettings && !('activeDate' in accountSettings)) {
				violations.push('An active member should have an active date');
			}
		}

		if (accountSettings.status === 'Inactive') {
			if (memberships && memberships.length > 0) {
				violations.push('Inactive account should not have a membership.');
			}
			if (linkedAccounts.length > 0) {
				violations.push('Inactive account should not have linked accounts.');
			}
			if (!accountSettings.inactiveDate) {
				violations.push('Inactive account should have an inactive date.');
			}
		}

		const VALID_LEGACY = ['student', 'leadership', 'level-0', 'level-1', 'level-3', 'level-3', 'staff', 'none'];
		const legacy = mas.accountSettings.legacy;
		if (!VALID_LEGACY.includes(legacy as string)) {
			violations.push('account must have a valid legacy selection.');
		}

		return {
			valid: violations.length === 0,
			violations,
		};
	}

	private _removeField(path: string): this {
		this.advancedDebugging.setLogStyle('color: pink; background: purple');
		const segments = path.split('.');
		let obj: any = this.raw; // 👈 This allows dynamic indexing

		for (let i = 0; i < segments.length - 1; i++) {
			obj = obj?.[segments[i]];
			if (!obj) return this;
		}

		const lastKey = segments[segments.length - 1];
		if (lastKey in obj) {
			this.advancedDebugging.log(`MASAccountModel_removeField=> ${path}`);
			delete obj[lastKey];
		}

		return this;
	}

	async logHistory(
		diff: Record<string, any>,
		accountId: string,
		changedBy: string,
		caller: string,
		autofix?: Record<string, any>,
	): Promise<void> {
		if (!accountId || !changedBy || Object.keys(diff).length === 0) return;
		this.advancedDebugging.setLogStyle('color: pink; background: purple');
		this.advancedDebugging.log('MASAccountModel=>logHistory', { accountId, caller });
		await this.accountService.appendAccountHistory(accountId, {
			changedBy,
			caller,
			changes: diff,
			autofix,
		});
	}
}

export class MASAccountExtrasModel {
	constructor(
		public account: MASAccountModel,
		private masAccountsService: MAS_Accounts_Service,
		public extras: {
			attendanceCount?: number;
			isHighlighted?: boolean;
			badgeColor?: string;
			programs?: MASProgramExtrasModel[];
		} = {},
	) {}

	/** Stable row id for PrimeNG `dataKey`. */
	get id(): string {
		return this.account.id;
	}

	get isBilling(): boolean {
		return this.account.isBilling;
	}

	get isMember(): boolean {
		return this.account.isMember;
	}

	static fromAccount(account: MASAccountModel, service: MAS_Accounts_Service): MASAccountExtrasModel {
		return new MASAccountExtrasModel(account, service);
	}

	getAttendanceCount(): Promise<number> {
		if (this.extras.attendanceCount !== undefined) {
			return Promise.resolve(this.extras.attendanceCount);
		}
		return this.masAccountsService.getAccountAttendanceTotal(this.account.id) ?? Promise.resolve(0);
	}

	async preloadAttendanceCount(): Promise<void> {
		const count = (await this.masAccountsService.getAccountAttendanceTotal(this.account.id)) ?? 0;
		this.extras.attendanceCount = count;
	}
}

/**
 * This is a Account Model with parent child hieracrhy
 */

export class MASParentAccountModel {
	constructor(public parent: MASAccountModel) {}

	get memberId(): string {
		return this.parent.raw.id;
	}

	get billingEmail(): string | null {
		return this.parent.raw.emailAddresses?.value ?? null;
	}

	get fullFamily(): MASAccountModel[] {
		return [this.parent, ...(this.parent.children ?? [])];
	}

	get allProgramIds(): string[] {
		const set = new Set<string>();
		this.fullFamily.forEach(account => {
			account.raw.memberships?.forEach(m => set.add(m));
		});
		return Array.from(set);
	}

	someMethod(): any {
		return 'futurevalue';
	}
}
